import React from "react";
import { View, StyleSheet, type StyleProp, type ViewStyle } from "react-native";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";

export type AxioCardVariant = "basic" | "interactive" | "ghost" | "workspace" | "workspace-collapsed";

export interface AxioCardProps {
  variant?: AxioCardVariant;
  isActive?: boolean;
  disabled?: boolean;
  hovered?: boolean;
  pressed?: boolean;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

const AxioCard: React.FC<AxioCardProps> = ({
  variant = "basic",
  isActive = false,
  disabled = false,
  hovered = false,
  pressed = false,
  style,
  children,
}) => {
  const computedStyle = getCardStyles({ variant, isActive, disabled, hovered, pressed });

  return <View style={[computedStyle, style]}>{children}</View>;
};

interface StyleParams {
  variant: AxioCardVariant;
  isActive: boolean;
  disabled: boolean;
  hovered: boolean;
  pressed: boolean;
}

const getCardStyles = ({ variant, isActive, disabled, hovered, pressed }: StyleParams): ViewStyle => {
  if (variant === "workspace") {
    return {
      ...styles.workspaceBase,
      ...(isActive ? styles.workspaceActive : null),
      ...(hovered && !isActive && !disabled ? styles.workspaceHovered : null),
      ...(pressed && !disabled ? styles.workspacePressed : null),
      ...(disabled ? styles.workspaceDisabled : null),
    };
  }

  if (variant === "workspace-collapsed") {
    return {
      ...styles.workspaceBaseCollapsed,
      ...(isActive ? styles.workspaceActive : null),
      ...(hovered && !isActive && !disabled ? styles.workspaceHovered : null),
      ...(pressed && !disabled ? styles.workspacePressed : null),
      ...(disabled ? styles.workspaceDisabled : null),
    };
  }

  if (variant === "ghost") {
    return {
      ...styles.basicBase,
      backgroundColor: colors.grey[25],
      borderWidth: 0,
      shadowOpacity: 0,
    };
  }

  if (variant === "interactive") {
    return {
      ...styles.basicBase,
      ...(hovered ? styles.interactiveHovered : null),
      ...(pressed ? styles.interactivePressed : null),
    };
  }

  // basic
  return styles.basicBase;
};

const styles = StyleSheet.create({
  basicBase: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    ...webTransitions.fast,
  },
  interactiveHovered: {
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
    transform: [{ translateY: -2 }],
  },
  interactivePressed: {
    transform: [{ translateY: 0 }],
    shadowOpacity: 0.04,
  },
  // Workspace card variant matches the sidebar workspace card container
  workspaceBase: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    ...webTransitions.fast,
  },
  workspaceBaseCollapsed: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xs,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    ...webTransitions.fast,
  },
  workspaceActive: {
    backgroundColor: colors.brand[50],
  },
  workspaceHovered: {
    backgroundColor: colors.grey[50],
  },
  workspacePressed: {},
  workspaceDisabled: {
    opacity: 0.5,
  },
});

export default AxioCard;
